/**
 * Zajednički prikaz jednog obaveštenja za header dropdown, admin dashboard i sl.
 */
import React, { useMemo } from 'react';
import { buildNotificationContext } from './buildNotificationContext';
import { getNotificationTypeLabel, formatNotificationDateCompact } from '@/shared/utils/notificationDisplay';
import NotificationRowContent, { MARK_READ_ICON } from './NotificationRowContent';
import { useNotificationItemActions } from './useNotificationItemActions';
import { NOTIFICATION_ITEM_UI } from './notificationItemUi';

export function NotificationItem({
  notification,
  variant = 'header',
  ctx: ctxProp,
  isAdmin = false,
  loginOrganizations = null,
  obligationsInPreparation = [],
  obligations = [],
  organizations = [],
  organizationNameById = {},
  onResolved,
  onModeratorAssignmentChanged,
  onLinkNavigate,
}) {
  const type = notification?.notification_type || notification?.type;
  const ui = NOTIFICATION_ITEM_UI[variant === 'dashboard' ? 'dashboard' : 'header'];

  const ctx = useMemo(
    () =>
      ctxProp ??
      buildNotificationContext({
        isAdmin,
        loginOrganizations,
        obligations,
        obligationsInPreparation,
        organizations,
        organizationNameById,
      }),
    [
      ctxProp,
      isAdmin,
      loginOrganizations,
      obligations,
      obligationsInPreparation,
      organizations,
      organizationNameById,
    ]
  );

  const {
    busy,
    moderatorLoading,
    handleMarkRead,
    handleAcceptModeration,
    handleRejectModeration,
  } = useNotificationItemActions(notification, { onResolved, onModeratorAssignmentChanged });

  const sentAt = notification?.created_at || notification?.created || notification?.sent_at;
  const timeLabel =
    String(notification?.time ?? notification?.relative_time ?? '').trim() ||
    (sentAt ? formatNotificationDateCompact(sentAt) : null);

  const markReadBusy = busy && !moderatorLoading;
  const showDashboardMarkRead = variant === 'dashboard';

  const inner = (
    <div className={ui.inner}>
      <div className={ui.head}>
        {showDashboardMarkRead ? (
          <div className={ui.headMain}>
            <span className={ui.type}>{getNotificationTypeLabel(type)}</span>
          </div>
        ) : (
          <span className={ui.type}>{getNotificationTypeLabel(type)}</span>
        )}
        {showDashboardMarkRead ? (
          <div className={ui.headEnd}>
            {timeLabel ? <span className={ui.time}>{timeLabel}</span> : null}
            <button
              type="button"
              className={ui.markReadBtnHead}
              onClick={handleMarkRead}
              disabled={busy || !!moderatorLoading}
              aria-label={markReadBusy ? 'Чување…' : 'Означи као прочитано'}
              title={markReadBusy ? 'Чување…' : 'Означи као прочитано'}
            >
              <span className={ui.markReadIcon} aria-hidden>
                {MARK_READ_ICON}
              </span>
            </button>
          </div>
        ) : timeLabel ? (
          <span className={ui.time}>{timeLabel}</span>
        ) : null}
      </div>
      <NotificationRowContent
        notification={notification}
        ctx={ctx}
        variant={variant}
        busy={busy}
        moderatorLoading={moderatorLoading}
        onMarkRead={showDashboardMarkRead ? undefined : handleMarkRead}
        onAcceptModeration={handleAcceptModeration}
        onRejectModeration={handleRejectModeration}
        onLinkNavigate={onLinkNavigate}
      />
    </div>
  );

  if (variant === 'dashboard') {
    return (
      <div className={ui.item}>
        <div className="dashboard-notification-dot" aria-hidden />
        {inner}
      </div>
    );
  }

  return (
    <li className={ui.item}>
      {inner}
    </li>
  );
}

export default NotificationItem;
